<script setup>
    import { onMounted, ref, nextTick, watch, defineOptions, defineProps, defineEmits } from 'vue';
    import { MarkerClusterer } from "@googlemaps/markerclusterer";
    import { timeSimplified, utcToLocal } from '@/tools/TimeFunctions';

// Set up Google Maps (only once)
if (!window.googleMapsInitialized) {
    window.googleMapsInitialized = true;
    (g=>{var h,a,k,p="The Google Maps JavaScript API",c="google",l="importLibrary",q="__ib__",m=document,b=window;b=b[c]||(b[c]={});var d=b.maps||(b.maps={}),r=new Set,e=new URLSearchParams,u=()=>h||(h=new Promise((f,n)=>{a=m.createElement("script");e.set("libraries",[...r]+"");for(k in g)e.set(k.replace(/[A-Z]/g,t=>"_"+t[0].toLowerCase()),g[k]);e.set("callback",c+".maps."+q);a.src=`https://maps.${c}apis.com/maps/api/js?`+e;d[q]=f;a.onerror=()=>h=n(Error(p+" could not load."));a.nonce=m.querySelector("script[nonce]")?.nonce||"";m.head.append(a)}));d[l]?console.warn(p+" only loads once."):d[l]=(f,...n)=>r.add(f)&&u().then(()=>d[l](f,...n))})({
        key: process.env.VUE_APP_GOOGLE_MAPS_API_KEY || "AIzaSyAdnZ7SDSCwfssmTuoztLkglh9w06vbPg8",
        v: "weekly",
    });
}

    /* global google */
    defineOptions({
        name: 'GoogleMap'
    });

    const props = defineProps({
        unitData: {
            type: Array,
            default: () => []
        },
        timeFormat: {
            type: String,
            default: '24hour'
        },
        subEntities: {
            type: Array,
            default: () => []
        },
        entityData: {
            type: Object,
            default: () => ({})
        },
        deviceClasses: {
            type: Array,
            default: () => []
        },
        unitClassMap: {
            type: Object,
            default: () => ({})
        },
        alertSeverityMap: {
            type: Object,
            default: () => ({})
        },
        quickMetricSpecsMap: {
            type: Object,
            default: () => ({})
        },
        /** When false, the map shows but no pins/clusters are drawn (e.g. while dashboard data loads). */
        markersReady: {
            type: Boolean,
            default: true,
        },
    });

    const emit = defineEmits(['navigate-to-unit']);

    const mapContainer = ref(null);
    const control = ref(null);
    let map;
    let infoWindow;
    let clusterer = null;
    let currentMarkers = [];
    // keyed by unit ID for O(1) diff lookups
    let markerMap = new Map(); // id -> { marker, lastLat, lastLng, lastOnline, lastClassId, lastAlertSeverity, lastAnimationClass, lastEffectColor }

    // Clear all existing markers from the map
    function clearMarkers() {
        currentMarkers.forEach(m => { m.map = null; });
        currentMarkers = [];
        markerMap.clear();
        if (clusterer) {
            clusterer.clearMarkers();
            clusterer = null;
        }
    }

    /** Clears pins when not ready; otherwise redraws from current props. */
    function updateMarkers() {
        if (!map || !infoWindow) return;
        if (!props.markersReady) {
            clearMarkers();
            return;
        }
        drawMarkers(props.unitData);
    }

    function createAlertBadge(alertSeverity) {
        if (!alertSeverity || alertSeverity === 'info') return null;
        const isCritical = alertSeverity === 'critical';
        const badge = document.createElement('span');
        badge.title = isCritical ? 'Critical alert' : 'Warning alert';
        badge.style.cssText = `
            position: absolute;
            top: -4px;
            right: -4px;
            width: 16px;
            height: 16px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            line-height: 1;
            filter: drop-shadow(0 1px 2px rgba(0,0,0,0.35));
        `;

        if (isCritical) {
            badge.style.borderRadius = '50%';
            badge.style.border = '2px solid #fff';
            badge.style.background = '#ef4444';
            badge.style.color = '#fff';
            badge.style.fontSize = '11px';
            badge.style.fontWeight = '800';
            badge.textContent = '!';
            return badge;
        }

        badge.innerHTML = `
            <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
                <path d="M10 2.6L18 16.8a1 1 0 01-.87 1.5H2.87a1 1 0 01-.87-1.5L10 2.6z"
                    fill="#facc15" stroke="#78350f" stroke-width="1.2" stroke-linejoin="round" />
                <path d="M10 7.2v4.8" stroke="#78350f" stroke-width="1.8" stroke-linecap="round" />
                <circle cx="10" cy="14.5" r="1.1" fill="#78350f" />
            </svg>
        `;
        return badge;
    }

    function escapeHtml(value) {
        return String(value ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    function normalizeAlertNames(alerts) {
        if (!Array.isArray(alerts)) return [];
        return alerts
            .map((alert) => (typeof alert === 'string' ? alert.trim() : String(alert ?? '').trim()))
            .filter(Boolean);
    }

    function formatMetricValue(value, unit = '') {
        if (typeof value === 'boolean') return value ? 'On' : 'Off';
        if (typeof value === 'number' && Number.isFinite(value)) {
            const display = Number.isInteger(value)
                ? `${value}`
                : value.toFixed(Math.abs(value) < 10 ? 2 : 1).replace(/\.0+$/, '').replace(/(\.\d*[1-9])0+$/, '$1');
            return `${display}${unit ? ` ${unit}` : ''}`;
        }
        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (!trimmed) return '';
            return `${trimmed}${unit ? ` ${unit}` : ''}`;
        }
        if (value == null) return '';
        return `${String(value)}${unit ? ` ${unit}` : ''}`;
    }

    function parseComparableNumber(value) {
        if (typeof value === 'number' && Number.isFinite(value)) return value;
        if (typeof value === 'boolean') return value ? 1 : 0;
        if (typeof value === 'string') {
            const trimmed = value.trim().toLowerCase();
            if (trimmed === 'true') return 1;
            if (trimmed === 'false') return 0;
            const parsed = Number.parseFloat(trimmed);
            return Number.isFinite(parsed) ? parsed : null;
        }
        return null;
    }

    function normalizeHexColor(value, fallback = '#3b82f6') {
        const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
        return /^#[0-9a-f]{6}$/.test(normalized) ? normalized : fallback;
    }

    function hexToRgbTuple(value) {
        const hex = normalizeHexColor(value, '#3b82f6').slice(1);
        const r = Number.parseInt(hex.slice(0, 2), 16);
        const g = Number.parseInt(hex.slice(2, 4), 16);
        const b = Number.parseInt(hex.slice(4, 6), 16);
        return `${r}, ${g}, ${b}`;
    }

    function isSnapshotMetricEntry(value) {
        return value && typeof value === 'object' && !Array.isArray(value) && Object.prototype.hasOwnProperty.call(value, 'value');
    }

    function snapshotMetricRawValue(value) {
        return isSnapshotMetricEntry(value) ? value.value : value;
    }

    function hasSnapshotMetricValue(value) {
        const raw = snapshotMetricRawValue(value);
        return raw !== undefined && raw !== null && raw !== '';
    }

    function deriveSnapshotMetricSpecs(snapshots) {
        if (!snapshots || typeof snapshots !== 'object' || Array.isArray(snapshots)) return [];
        const specs = [];
        for (const key of Object.keys(snapshots).sort((a, b) => a.localeCompare(b))) {
            if (!key.includes('.')) continue;
            const splitIdx = key.indexOf('.');
            const source = key.slice(0, splitIdx).trim();
            const field = key.slice(splitIdx + 1).trim();
            if (!source || !field) continue;
            const snapshotValue = snapshots[key];
            specs.push({
                source,
                field,
                friendlyName: (typeof snapshotValue?.friendlyName === 'string' && snapshotValue.friendlyName.trim())
                    ? snapshotValue.friendlyName
                    : field,
                unit: typeof snapshotValue?.unit === 'string' ? snapshotValue.unit : '',
                mapAnimationRule: (snapshotValue?.mapAnimationRule && typeof snapshotValue.mapAnimationRule === 'object' && !Array.isArray(snapshotValue.mapAnimationRule))
                    ? snapshotValue.mapAnimationRule
                    : null,
            });
        }
        return specs;
    }

    function snapshotValueForSpec(data, spec) {
        const snapshots = data?.snapshots;
        if (!snapshots || typeof snapshots !== 'object' || Array.isArray(snapshots)) return null;
        const source = typeof spec?.source === 'string' ? spec.source.trim() : '';
        const field = typeof spec?.field === 'string' ? spec.field.trim() : '';
        if (!field) return null;

        if (source) {
            const direct = snapshots[source]?.[field];
            if (hasSnapshotMetricValue(direct)) return snapshotMetricRawValue(direct);
            const key = `${source}.${field}`;
            if (key in snapshots && hasSnapshotMetricValue(snapshots[key])) return snapshotMetricRawValue(snapshots[key]);
            return null;
        }

        if (field in snapshots && hasSnapshotMetricValue(snapshots[field])) return snapshotMetricRawValue(snapshots[field]);
        const dotted = Object.keys(snapshots).find((key) => key.endsWith(`.${field}`));
        return (dotted && hasSnapshotMetricValue(snapshots[dotted])) ? snapshotMetricRawValue(snapshots[dotted]) : null;
    }

    function snapshotEntryForSpec(data, spec) {
        const snapshots = data?.snapshots;
        if (!snapshots || typeof snapshots !== 'object' || Array.isArray(snapshots)) return null;
        const source = typeof spec?.source === 'string' ? spec.source.trim() : '';
        const field = typeof spec?.field === 'string' ? spec.field.trim() : '';
        if (!field) return null;

        if (source) {
            const nested = snapshots[source]?.[field];
            if (isSnapshotMetricEntry(nested)) return nested;
            const dottedKey = `${source}.${field}`;
            const dotted = snapshots[dottedKey];
            return isSnapshotMetricEntry(dotted) ? dotted : null;
        }

        if (isSnapshotMetricEntry(snapshots[field])) return snapshots[field];
        const dotted = Object.keys(snapshots).find((key) => key.endsWith(`.${field}`) && isSnapshotMetricEntry(snapshots[key]));
        return dotted ? snapshots[dotted] : null;
    }

    function ruleMatchesValue(rule, rawValue) {
        const metricValue = parseComparableNumber(rawValue);
        if (metricValue == null || !rule || typeof rule !== 'object') return false;

        if (rule.operator === 'between') {
            const min = parseComparableNumber(rule.min);
            const max = parseComparableNumber(rule.max);
            if (min == null || max == null) return false;
            return metricValue >= min && metricValue <= max;
        }

        const ruleValue = parseComparableNumber(rule.value);
        if (ruleValue == null) return false;
        if (rule.operator === '=') return metricValue === ruleValue;
        if (rule.operator === '!=') return metricValue !== ruleValue;
        if (rule.operator === '<') return metricValue < ruleValue;
        if (rule.operator === '<=') return metricValue <= ruleValue;
        if (rule.operator === '>') return metricValue > ruleValue;
        if (rule.operator === '>=') return metricValue >= ruleValue;
        return false;
    }

    function mapAnimationEffectForUnit(unitId, data) {
        const configuredSpecs = Array.isArray(props.quickMetricSpecsMap?.[unitId]) ? props.quickMetricSpecsMap[unitId] : [];
        const specs = configuredSpecs.length > 0 ? configuredSpecs : deriveSnapshotMetricSpecs(data?.snapshots);
        if (!specs.length) return { className: '', color: '#3b82f6' };

        const matched = [];
        for (const spec of specs) {
            const snapshotEntry = snapshotEntryForSpec(data, spec);
            const snapshotRule = snapshotEntry?.mapAnimationRule;
            // Prefer explicit spec rule; fall back to snapshot-embedded rule for same source.field.
            const rule = spec?.mapAnimationRule || snapshotRule;
            if (!rule || typeof rule !== 'object' || rule.enabled === false) continue;
            const value = snapshotValueForSpec(data, spec);
            if (ruleMatchesValue(rule, value)) {
                matched.push({
                    className: `mesh-map-anim-${rule.animation || 'pulse'}`,
                    color: normalizeHexColor(rule.color, '#3b82f6'),
                });
            }
        }
        if (matched.length !== 1) {
            if (matched.length > 1) {
                console.warn(`GoogleMap: Multiple animation rules matched for unit ${unitId}; suppressing animation.`);
            }
            return { className: '', color: '#3b82f6' };
        }
        return matched[0];
    }

    function quickMetricsHtmlForUnit(unitId, data) {
        const snapshots = data?.snapshots;
        if (!snapshots || typeof snapshots !== 'object' || Array.isArray(snapshots)) return '';
        const configuredSpecs = Array.isArray(props.quickMetricSpecsMap?.[unitId]) ? props.quickMetricSpecsMap[unitId] : [];
        const specs = configuredSpecs.length > 0 ? configuredSpecs : deriveSnapshotMetricSpecs(snapshots);
        if (specs.length === 0) return '';

        const rows = [];
        for (const spec of specs) {
            const field = typeof spec?.field === 'string' ? spec.field : '';
            if (!field) continue;
            const value = formatMetricValue(snapshotValueForSpec(data, spec), spec?.unit || '');
            if (!value) continue;
            const label = spec?.friendlyName || spec?.label || field;
            rows.push(`
                <span style="display:inline-flex;flex-direction:column;gap:2px;min-width:72px;padding:4px 6px;border-radius:8px;border:1px solid #e5e7eb;background:#f8fafc;">
                    <span style="font-size:10px;font-weight:700;letter-spacing:.02em;color:#6b7280;text-transform:uppercase;line-height:1.1;">${escapeHtml(label)}</span>
                    <span style="font-size:11px;font-weight:700;color:#111827;line-height:1.2;">${escapeHtml(value)}</span>
                </span>
            `);
            if (rows.length >= 4) break;
        }

        if (rows.length === 0) return '';
        return `
            <div style="display:flex;flex-direction:column;gap:6px;padding:8px 0;border-top:1px solid #f1f5f9;">
                <span style="font-size:10px;font-weight:700;letter-spacing:.03em;color:#6b7280;text-transform:uppercase;">Quick metrics</span>
                <div style="display:flex;flex-wrap:wrap;gap:6px;">
                    ${rows.join('')}
                </div>
            </div>
        `;
    }

    const CLASS_ICON_SIZE_PX = 60;
    const STATUS_DOT_SIZE_PX = 16;
    const FALLBACK_PIN_SCALE = 1.4;

    function buildMarkerContent(classIconUrl, statusColor, alertSeverity, animationClass = '', effectColor = '#3b82f6') {
        if (classIconUrl) {
            const wrapper = document.createElement('div');
            wrapper.style.cssText = `
                position: relative;
                width: ${CLASS_ICON_SIZE_PX}px;
                height: ${CLASS_ICON_SIZE_PX}px;
                cursor: pointer;
                filter: drop-shadow(0 2px 5px rgba(0,0,0,0.45));
            `;
            wrapper.style.setProperty('--mesh-map-glow-rgb', hexToRgbTuple(effectColor));
            const iconLayer = document.createElement('div');
            iconLayer.style.cssText = `
                position: relative;
                width: ${CLASS_ICON_SIZE_PX}px;
                height: ${CLASS_ICON_SIZE_PX}px;
                display: inline-flex;
                align-items: center;
                justify-content: center;
            `;
            if (animationClass) iconLayer.classList.add(animationClass);
            const img = document.createElement('img');
            img.src = classIconUrl;
            img.style.cssText = `width: ${CLASS_ICON_SIZE_PX}px; height: ${CLASS_ICON_SIZE_PX}px; object-fit: contain;`;
            img.onerror = () => { img.style.display = 'none'; };
            const dot = document.createElement('span');
            dot.style.cssText = `
                position: absolute;
                bottom: -2px;
                right: -2px;
                width: ${STATUS_DOT_SIZE_PX}px;
                height: ${STATUS_DOT_SIZE_PX}px;
                border-radius: 50%;
                border: 3px solid #fff;
                background: ${statusColor};
                box-shadow: 0 1px 3px rgba(0,0,0,0.3);
            `;
            iconLayer.appendChild(img);
            wrapper.appendChild(iconLayer);
            wrapper.appendChild(dot);
            const alertBadge = createAlertBadge(alertSeverity);
            if (alertBadge) wrapper.appendChild(alertBadge);
            return wrapper;
        } else {
            const wrapper = document.createElement('div');
            wrapper.style.cssText = 'position: relative; cursor: pointer;';
            wrapper.style.setProperty('--mesh-map-glow-rgb', hexToRgbTuple(effectColor));
            const pin = new google.maps.marker.PinElement({
                background: statusColor,
                borderColor: statusColor,
                glyphColor: '#ffffff',
                scale: FALLBACK_PIN_SCALE,
            });
            if (animationClass) pin.element.classList.add(animationClass);
            wrapper.appendChild(pin.element);
            const alertBadge = createAlertBadge(alertSeverity);
            if (alertBadge) wrapper.appendChild(alertBadge);
            return wrapper;
        }
    }

    function attachClickListener(marker, key) {
        marker.addListener('click', () => {
            try {
                const data = marker._meshData || {};
                const isOnline = data.lastActivityUTC &&
                    timeSimplified(utcToLocal(data.lastActivityUTC, props.timeFormat)) === 'Current';
                const statusColor  = isOnline ? '#16a34a' : '#9ca3af';
                const statusBg     = isOnline ? '#dcfce7' : '#f3f4f6';
                const statusLabel  = isOnline ? 'Online' : (data.lastActivityUTC ? 'Offline' : 'Undeployed');
                const alertSeverity = props.alertSeverityMap[key] || null;
                const criticalAlertNames = normalizeAlertNames(data?.alerts?.critical);
                const warningAlertNames = normalizeAlertNames(data?.alerts?.warning);
                const primaryAlertName = alertSeverity === 'critical'
                    ? (criticalAlertNames[0] || warningAlertNames[0] || '')
                    : (warningAlertNames[0] || criticalAlertNames[0] || '');
                const additionalAlertCount = Math.max(0, (criticalAlertNames.length + warningAlertNames.length) - (primaryAlertName ? 1 : 0));
                const alertNameLabel = primaryAlertName
                    ? `${primaryAlertName}${additionalAlertCount > 0 ? ` +${additionalAlertCount}` : ''}`
                    : '';
                const alertNameRow = alertNameLabel
                    ? `<div style="display:flex;align-items:center;gap:8px;margin-top:8px;">
                        ${alertSeverity === 'critical'
                            ? '<span style="display:inline-flex;align-items:center;justify-content:center;width:14px;height:14px;border-radius:999px;background:#ef4444;color:#fff;font-size:10px;font-weight:800;line-height:1;">!</span>'
                            : `<span style="display:inline-flex;align-items:center;justify-content:center;width:14px;height:14px;line-height:1;">
                                <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true">
                                    <path d="M10 2.6L18 16.8a1 1 0 01-.87 1.5H2.87a1 1 0 01-.87-1.5L10 2.6z" fill="#facc15" stroke="#78350f" stroke-width="1.2" stroke-linejoin="round" />
                                    <path d="M10 7.2v4.8" stroke="#78350f" stroke-width="1.8" stroke-linecap="round" />
                                    <circle cx="10" cy="14.5" r="1.1" fill="#78350f" />
                                </svg>
                            </span>`
                        }
                        <span style="font-size:12px;color:#374151;line-height:1.4;word-break:break-word;">${escapeHtml(alertNameLabel)}</span>
                    </div>`
                    : '';
                const quickMetricsBlock = quickMetricsHtmlForUnit(key, data);

                let subEntitiesHTML = '';
                if (data.entities && props.subEntities && props.entityData) {
                    const names = props.subEntities
                        .filter(id => data.entities.includes(id))
                        .map(id => props.entityData[id]?.name || id)
                        .filter(Boolean);
                    if (names.length > 0) {
                        subEntitiesHTML = `
                            <div style="display:flex;flex-wrap:wrap;gap:4px;margin-top:8px;">
                                ${names.map(n =>
                                    `<span style="background:#eff6ff;color:#1d4ed8;padding:2px 10px;border-radius:999px;font-size:11px;font-weight:600;border:1px solid #bfdbfe;">${n}</span>`
                                ).join('')}
                            </div>`;
                    }
                }

                const cloudAppURL = typeof data.cloudAppURL === 'string' ? data.cloudAppURL.trim() : '';
                const safeCloudAppURL = escapeHtml(cloudAppURL);
                const cloudAppRow = cloudAppURL ? `
                    <div style="display:flex;align-items:center;gap:8px;padding:7px 0;border-top:1px solid #f1f5f9;">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                        <a href="${safeCloudAppURL}" target="_blank" rel="noopener noreferrer"
                           style="color:#2563eb;font-size:12px;text-decoration:none;word-break:break-all;line-height:1.4;"
                           onmouseover="this.style.textDecoration='underline'" onmouseout="this.style.textDecoration='none'">
                            ${safeCloudAppURL}
                        </a>
                    </div>` : '';

                const lastActive = data.lastActivityUTC
                    ? utcToLocal(data.lastActivityUTC, props.timeFormat)
                    : 'Unknown';

                const unitID = escapeHtml(data.friendlyName || key);
                const safeLat = escapeHtml(parseFloat(data.currentLat).toFixed(5));
                const safeLon = escapeHtml(parseFloat(data.currentLon).toFixed(5));
                const safeLastActive = escapeHtml(lastActive);

                infoWindow.setContent(`
                    <div style="font-family:system-ui,sans-serif;width:260px;padding:0;overflow:hidden;border-radius:12px;">

                        <!-- Header -->
                        <div style="padding:14px 16px 10px;">
                            <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:8px;">
                                <span style="font-size:15px;font-weight:700;color:#111827;line-height:1.3;word-break:break-word;">${unitID}</span>
                                <span style="flex-shrink:0;display:inline-flex;align-items:center;gap:4px;background:${statusBg};color:${statusColor};padding:2px 8px;border-radius:999px;font-size:11px;font-weight:700;white-space:nowrap;">
                                    <span style="width:6px;height:6px;border-radius:50%;background:${statusColor};display:inline-block;"></span>
                                    ${statusLabel}
                                </span>
                            </div>
                            ${alertNameRow}
                            ${subEntitiesHTML}
                        </div>

                        <!-- Divider -->
                        <div style="height:1px;background:#f1f5f9;margin:0 16px;"></div>

                        <!-- Details -->
                        <div style="padding:10px 16px;display:flex;flex-direction:column;gap:0;">
                            <div style="display:flex;align-items:center;gap:8px;padding:7px 0;border-top:1px solid transparent;">
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                                <span style="font-size:12px;color:#374151;">${safeLat}, ${safeLon}</span>
                            </div>
                            <div style="display:flex;align-items:center;gap:8px;padding:7px 0;border-top:1px solid #f1f5f9;">
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                <span style="font-size:12px;color:#374151;">${safeLastActive}</span>
                            </div>
                            ${cloudAppRow}
                            ${quickMetricsBlock}
                        </div>

                        <!-- Footer button -->
                        <div style="padding:0 16px 14px;">
                            <button id="viewUnitDashboardButton"
                                style="width:100%;padding:8px 0;background:#2563eb;color:#fff;border:none;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;transition:background 0.15s;"
                                onmouseover="this.style.background='#1d4ed8'" onmouseout="this.style.background='#2563eb'">
                                View Dashboard
                            </button>
                        </div>
                    </div>
                `);
                infoWindow.open(map, marker);
                google.maps.event.addListenerOnce(infoWindow, 'domready', () => {
                    const btn = document.getElementById('viewUnitDashboardButton');
                    if (btn) {
                        btn.addEventListener('click', () => {
                            if (key && key !== 'null' && key !== 'undefined') {
                                emit('navigate-to-unit', key);
                            }
                        });
                    }
                });
            } catch (e) {
                console.error('GoogleMap: Error opening info window:', e);
            }
        });
    }

    // Build and place markers for the given unitData array.
    // Diffs against existing markers so unchanged pins are not recreated.
    function drawMarkers(unitData) {
        if (!map || !infoWindow) return;

        const incomingIds = new Set();
        let clusterStyleChanged = false;

        try {
            for (const [key, value] of unitData) {
                const data = value;

                if (!data.currentLat || data.currentLat === '' ||
                    !data.currentLon || data.currentLon === '' ||
                    isNaN(parseFloat(data.currentLat)) ||
                    isNaN(parseFloat(data.currentLon))) {
                    continue;
                }

                const lat = parseFloat(data.currentLat);
                const lng = parseFloat(data.currentLon);

                if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
                    console.warn(`GoogleMap: Invalid coordinates for unit ${key}`);
                    continue;
                }

                const isOnline = data.lastActivityUTC &&
                    timeSimplified(utcToLocal(data.lastActivityUTC, props.timeFormat)) === 'Current';
                const classId = props.unitClassMap[key] ?? null;
                const classIconUrl = classId
                    ? (props.deviceClasses.find(c => c.id === classId)?.iconUrl || null)
                    : null;
                const statusColor = isOnline ? '#22c55e' : '#9ca3af';
                const alertSeverity = props.alertSeverityMap[key] || null;
                const animationEffect = mapAnimationEffectForUnit(key, data);
                const animationClass = animationEffect.className;
                const effectColor = animationEffect.color;

                incomingIds.add(key);
                const existing = markerMap.get(key);

                if (existing) {
                    // Only update what changed - no DOM recreation unless necessary
                    const posChanged = existing.lastLat !== lat || existing.lastLng !== lng;
                    const styleChanged =
                        existing.lastOnline !== isOnline ||
                        existing.lastClassId !== classId ||
                        existing.lastAlertSeverity !== alertSeverity ||
                        existing.lastAnimationClass !== animationClass ||
                        existing.lastEffectColor !== effectColor;

                    if (posChanged) {
                        existing.marker.position = { lat, lng };
                        existing.lastLat = lat;
                        existing.lastLng = lng;
                    }
                    if (styleChanged) {
                        existing.marker.content = buildMarkerContent(classIconUrl, statusColor, alertSeverity, animationClass, effectColor);
                        existing.marker._meshMeta = {
                            classId: classId || null,
                            className: classId ? (props.deviceClasses.find(c => c.id === classId)?.name || 'Unknown') : null,
                            classIconUrl: classIconUrl || null,
                            isOnline,
                            alertSeverity: alertSeverity || null,
                        };
                        existing.lastOnline = isOnline;
                        existing.lastClassId = classId;
                        existing.lastAlertSeverity = alertSeverity;
                        existing.lastAnimationClass = animationClass;
                        existing.lastEffectColor = effectColor;
                        clusterStyleChanged = true;
                    }
                    existing.marker._meshData = data;
                    continue;
                }

                // New marker
                const marker = new google.maps.marker.AdvancedMarkerElement({
                    position: { lat, lng },
                    map,
                    content: buildMarkerContent(classIconUrl, statusColor, alertSeverity, animationClass, effectColor),
                });

                marker._meshMeta = {
                    classId: classId || null,
                    className: classId ? (props.deviceClasses.find(c => c.id === classId)?.name || 'Unknown') : null,
                    classIconUrl: classIconUrl || null,
                    isOnline,
                    alertSeverity: alertSeverity || null,
                };
                marker._meshData = data;

                attachClickListener(marker, key);

                markerMap.set(key, {
                    marker,
                    lastLat: lat,
                    lastLng: lng,
                    lastOnline: isOnline,
                    lastClassId: classId,
                    lastAlertSeverity: alertSeverity,
                    lastAnimationClass: animationClass,
                    lastEffectColor: effectColor,
                });
                currentMarkers.push(marker);
            }
        } catch (e) {
            console.error('GoogleMap: Error creating markers:', e);
        }

        // Remove markers for units no longer in the filtered list
        for (const [id, entry] of markerMap) {
            if (!incomingIds.has(id)) {
                entry.marker.map = null;
                markerMap.delete(id);
                currentMarkers = currentMarkers.filter(m => m !== entry.marker);
            }
        }

        // Rebuild clusterer only when the marker count changed (add/remove), not on position/style updates
        const clusterMarkerCount = Array.isArray(clusterer?.markers) ? clusterer.markers.length : 0;
        const clusterNeedsRefresh = !clusterer || clusterStyleChanged || currentMarkers.length !== clusterMarkerCount;
        if (!clusterNeedsRefresh) return;

        if (clusterer) {
            clusterer.clearMarkers();
            clusterer = null;
        }

        if (currentMarkers.length) {
            try {
                const clusterRenderer = {
                    render({ count, position, markers }) {
                        // Tally units per class from marker metadata
                        const tally = new Map(); // classId|'unclassified' -> { name, iconUrl, online, offline, critical, warning }
                        let clusterCritical = 0;
                        let clusterWarning = 0;
                        for (const m of markers) {
                            const meta = m._meshMeta || {};
                            const key = meta.classId || 'unclassified';
                            if (!tally.has(key)) {
                                tally.set(key, {
                                    name: meta.className || 'Unclassified',
                                    iconUrl: meta.classIconUrl || null,
                                    online: 0,
                                    offline: 0,
                                    critical: 0,
                                    warning: 0,
                                });
                            }
                            const entry = tally.get(key);
                            meta.isOnline ? entry.online++ : entry.offline++;
                            if (meta.alertSeverity === 'critical') {
                                entry.critical++;
                                clusterCritical++;
                            } else if (meta.alertSeverity === 'warning') {
                                entry.warning++;
                                clusterWarning++;
                            }
                        }

                        // Build tooltip HTML
                        const rows = [...tally.entries()].map(([, entry]) => {
                            const iconHtml = entry.iconUrl
                                ? `<img src="${entry.iconUrl}" style="width:22px;height:22px;object-fit:contain;border-radius:4px;vertical-align:middle;margin-right:7px;flex-shrink:0;">`
                                : `<span style="display:inline-block;width:16px;height:16px;border-radius:50%;background:#9ca3af;margin-right:8px;vertical-align:middle;flex-shrink:0;"></span>`;
                            const onlineDot = entry.online > 0
                                ? `<span style="display:inline-flex;align-items:center;gap:4px;color:#111827;"><span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:#22c55e;flex-shrink:0;"></span><span style="font-size:13px;font-weight:600;min-width:1ch;">${entry.online}</span></span>`
                                : '';
                            const offlineDot = entry.offline > 0
                                ? `<span style="display:inline-flex;align-items:center;gap:4px;margin-left:${entry.online > 0 ? '10px' : '0'};color:#111827;"><span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:#9ca3af;flex-shrink:0;"></span><span style="font-size:13px;font-weight:600;min-width:1ch;">${entry.offline}</span></span>`
                                : '';
                            const criticalDot = entry.critical > 0
                                ? `<span style="display:inline-flex;align-items:center;gap:4px;margin-left:${(entry.online > 0 || entry.offline > 0) ? '10px' : '0'};color:#111827;"><span style="display:inline-flex;align-items:center;justify-content:center;width:12px;height:12px;border-radius:999px;background:#ef4444;color:#fff;font-size:10px;font-weight:800;line-height:1;">!</span><span style="font-size:13px;font-weight:600;min-width:1ch;">${entry.critical}</span></span>`
                                : '';
                            const warningDot = entry.warning > 0
                                ? `<span style="display:inline-flex;align-items:center;gap:4px;margin-left:${(entry.online > 0 || entry.offline > 0 || entry.critical > 0) ? '10px' : '0'};color:#111827;">
                                    <svg viewBox="0 0 20 20" width="12" height="12" aria-hidden="true" style="flex-shrink:0;">
                                        <path d="M10 2.6L18 16.8a1 1 0 01-.87 1.5H2.87a1 1 0 01-.87-1.5L10 2.6z" fill="#facc15" stroke="#78350f" stroke-width="1.2" stroke-linejoin="round" />
                                        <path d="M10 7.2v4.8" stroke="#78350f" stroke-width="1.8" stroke-linecap="round" />
                                        <circle cx="10" cy="14.5" r="1.1" fill="#78350f" />
                                    </svg>
                                    <span style="font-size:13px;font-weight:600;min-width:1ch;">${entry.warning}</span>
                                </span>`
                                : '';
                            return `
                                <div style="display:flex;align-items:center;gap:6px;padding:4px 0;white-space:nowrap;border-bottom:1px solid #f3f4f6;">
                                    ${iconHtml}
                                    <span style="font-weight:600;font-size:13px;flex:1;color:#111;">${entry.name}</span>
                                    <span style="display:inline-flex;align-items:center;gap:2px;margin-left:8px;">${onlineDot}${offlineDot}${criticalDot}${warningDot}</span>
                                </div>
                            `;
                        }).join('');

                        const tooltip = document.createElement('div');
                        tooltip.style.cssText = `
                            display: none;
                            position: absolute;
                            bottom: calc(100% + 10px);
                            left: 50%;
                            transform: translateX(-50%);
                            background: #fff;
                            color: #111827;
                            border: 1px solid #e5e7eb;
                            border-radius: 10px;
                            padding: 10px 14px 6px;
                            box-shadow: 0 6px 24px rgba(0,0,0,0.18);
                            z-index: 9999;
                            min-width: 180px;
                            pointer-events: none;
                            font-family: sans-serif;
                            overflow: visible;
                        `;
                        tooltip.innerHTML = rows;

                        // Cluster bubble - soft white halo (no hard ring)
                        const bubble = document.createElement('div');
                        bubble.style.cssText = `
                            position: relative;
                            width: 52px;
                            height: 52px;
                            border-radius: 50%;
                            background: radial-gradient(circle at center, #3f52c4 0%, #3447ba 52%, rgba(255,255,255,0.55) 78%, rgba(255,255,255,0.12) 92%, rgba(255,255,255,0) 100%);
                            border: none;
                            box-shadow:
                                0 0 6px 2px rgba(255,255,255,0.45),
                                0 0 14px 6px rgba(255,255,255,0.2),
                                0 0 24px 10px rgba(255,255,255,0.08),
                                0 4px 14px rgba(0,0,0,0.35);
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            cursor: pointer;
                            font-weight: 700;
                            font-size: 16px;
                            color: #fff;
                            font-family: sans-serif;
                            overflow: visible;
                        `;
                        bubble.textContent = count;
                        if (clusterCritical > 0 || clusterWarning > 0) {
                            const badgeWrap = document.createElement('div');
                            badgeWrap.style.cssText = `
                                position: absolute;
                                top: -8px;
                                right: -8px;
                                display: inline-flex;
                                align-items: center;
                                gap: 4px;
                                pointer-events: none;
                            `;
                            if (clusterCritical > 0) {
                                const criticalBadge = document.createElement('span');
                                criticalBadge.style.cssText = `
                                    display:inline-flex;align-items:center;justify-content:center;
                                    width:18px;height:18px;border-radius:999px;
                                    border:1.5px solid #fff;background:#ef4444;color:#fff;
                                    font-size:10px;font-weight:800;line-height:1;
                                `;
                                criticalBadge.textContent = '!';
                                badgeWrap.appendChild(criticalBadge);
                            }
                            if (clusterWarning > 0) {
                                const warningBadge = document.createElement('span');
                                warningBadge.style.cssText = `
                                    display:inline-flex;align-items:center;justify-content:center;
                                    width:16px;height:16px;
                                    background:transparent;
                                    color:#78350f;
                                    line-height:1;
                                `;
                                warningBadge.innerHTML = `
                                    <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true">
                                        <path d="M10 2.6L18 16.8a1 1 0 01-.87 1.5H2.87a1 1 0 01-.87-1.5L10 2.6z" fill="#facc15" stroke="#78350f" stroke-width="1.2" stroke-linejoin="round" />
                                        <path d="M10 7.2v4.8" stroke="#78350f" stroke-width="1.8" stroke-linecap="round" />
                                        <circle cx="10" cy="14.5" r="1.1" fill="#78350f" />
                                    </svg>
                                `;
                                badgeWrap.appendChild(warningBadge);
                            }
                            bubble.appendChild(badgeWrap);
                        }
                        bubble.appendChild(tooltip);

                        const baseZIndex = Number(google.maps.Marker.MAX_ZINDEX) + count;
                        /** Above all other markers/clusters so the hover tooltip isn’t covered */
                        const hoverZIndex = Number(google.maps.Marker.MAX_ZINDEX) + 1_000_000 + count;

                        const clusterMarker = new google.maps.marker.AdvancedMarkerElement({
                            position,
                            content: bubble,
                            zIndex: baseZIndex,
                        });

                        bubble.addEventListener('mouseenter', () => {
                            tooltip.style.display = 'block';
                            clusterMarker.zIndex = hoverZIndex;
                        });
                        bubble.addEventListener('mouseleave', () => {
                            tooltip.style.display = 'none';
                            clusterMarker.zIndex = baseZIndex;
                        });

                        return clusterMarker;
                    }
                };

                clusterer = new MarkerClusterer({ markers: currentMarkers, map, renderer: clusterRenderer });

                // Only fit bounds on the initial draw (when there were no markers before)
                if (markerMap.size === currentMarkers.length) {
                    const bounds = new google.maps.LatLngBounds();
                    currentMarkers.forEach(m => bounds.extend(m.position));
                    map.fitBounds(bounds, 50);
                    google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
                        if (map.getZoom() > 15) map.setZoom(15);
                    });
                }
            } catch (e) {
                console.error('GoogleMap: Error setting up clustering:', e);
            }
        }
    }

    onMounted(async () => {
        await nextTick();

        let retryCount = 0;
        while (!mapContainer.value && retryCount < 10) {
            await new Promise(resolve => setTimeout(resolve, 100));
            retryCount++;
        }

        if (!mapContainer.value) {
            console.error('GoogleMap: Map container not available');
            return;
        }

        try {
            if (typeof google === 'undefined' || !google.maps) {
                throw new Error('Google Maps API not loaded');
            }

            const { Map } = await google.maps.importLibrary('maps');
            await google.maps.importLibrary('marker');

            map = new Map(mapContainer.value, {
                zoom: 5,
                center: { lat: 39.22696383208982, lng: -98.91715880586187 },
                mapId: 'DEMO_MAP_ID',
                mapTypeId: google.maps.MapTypeId.ROADMAP,
                mapTypeControl: true,
                mapTypeControlOptions: {
                    style: google.maps.MapTypeControlStyle.DROPDOWN_MENU,
                    position: google.maps.ControlPosition.TOP_RIGHT,
                },
                streetViewControl: false,
                fullscreenControl: false,
                zoomControlOptions: {
                    position: google.maps.ControlPosition.RIGHT_CENTER,
                },
                /** One-finger pan/zoom on touch; avoids “use two fingers” overlay on phones */
                gestureHandling: 'greedy',
            });

            await new Promise(resolve => google.maps.event.addListenerOnce(map, 'idle', resolve));

            infoWindow = new google.maps.InfoWindow({ content: '' });
            google.maps.event.addListener(map, 'click', () => infoWindow.close());

            const controlDiv = document.createElement('div');
            if (control.value) {
                controlDiv.appendChild(control.value);
                map.controls[google.maps.ControlPosition.TOP_LEFT].push(controlDiv);
            }

            updateMarkers();

        } catch (error) {
            console.error('GoogleMap: Error initializing map:', error);
            if (mapContainer.value) {
                mapContainer.value.innerHTML = `
                    <div class="flex items-center justify-center h-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                        <div class="text-center">
                            <p class="text-lg font-semibold mb-2">Map Loading Error</p>
                            <p class="text-sm">Unable to load Google Maps. Please refresh the page or try again later.</p>
                        </div>
                    </div>
                `;
            }
        }
    });

    // Redraw markers whenever the filtered unit list, class data, or ready flag changes.
    // Debounced so rapid stream bursts coalesce into a single redraw.
    let unitDataWatchTimer = null;
    watch(() => props.unitData, () => {
        clearTimeout(unitDataWatchTimer);
        unitDataWatchTimer = setTimeout(() => updateMarkers(), 150);
    }, { deep: true });

    watch(() => [props.deviceClasses, props.unitClassMap], () => {
        updateMarkers();
    }, { deep: true });

    watch(() => props.quickMetricSpecsMap, () => {
        updateMarkers();
    }, { deep: true });

    watch(() => props.markersReady, () => {
        updateMarkers();
    });

    const recenterMap = () => {
        try {
            if (!map) return;
            if (currentMarkers.length === 0) {
                map.setCenter({ lat: 39.22696383208982, lng: -98.91715880586187 });
                map.setZoom(5);
                return;
            }
            const bounds = new google.maps.LatLngBounds();
            currentMarkers.forEach(m => bounds.extend(m.position));
            map.fitBounds(bounds, 50);
            google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
                if (map.getZoom() > 15) map.setZoom(15);
            });
        } catch (error) {
            console.error('GoogleMap: Error recentering map:', error);
        }
    };
</script>

<template>
    <div class="relative h-full min-h-[200px] min-w-0 touch-manipulation">
        <div class="h-full w-full min-h-[inherit]" ref="mapContainer"></div>
        <div ref="control" class="mt-3 ml-3">
            <button
                type="button"
                @click="recenterMap"
                title="Recenter map"
                class="inline-flex items-center gap-1.5 min-h-[40px] px-4 py-2 rounded-full text-sm font-semibold bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 shadow-md border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 active:scale-95 transition-all duration-150 touch-manipulation"
            >
                <!-- crosshair/target icon -->
                <svg class="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="3"/>
                    <path stroke-linecap="round" d="M12 2v3M12 19v3M2 12h3M19 12h3"/>
                </svg>
                Recenter
            </button>
        </div>
    </div>
</template>

<style>
    .mesh-map-anim-pulse {
        animation: mesh-map-pulse 1.4s ease-in-out infinite;
    }

    .mesh-map-anim-bounce {
        animation: mesh-map-bounce 1s ease-in-out infinite;
    }

    .mesh-map-anim-glow {
        animation: mesh-map-glow 1.6s ease-in-out infinite;
    }

    .mesh-map-anim-ping {
        position: relative;
    }

    .mesh-map-anim-ping::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        width: 26px;
        height: 26px;
        border-radius: 50%;
        border: 2px solid rgba(var(--mesh-map-glow-rgb, 59, 130, 246), 0.85);
        transform: translate(-50%, -50%) scale(0.45);
        opacity: 0;
        pointer-events: none;
        animation: mesh-map-ping 1.2s ease-out infinite;
    }

    .mesh-map-anim-shake {
        animation: mesh-map-shake 0.7s linear infinite;
    }

    @keyframes mesh-map-pulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.12); }
    }

    @keyframes mesh-map-bounce {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-7px); }
    }

    @keyframes mesh-map-glow {
        0%, 100% { filter: drop-shadow(0 0 0 rgba(var(--mesh-map-glow-rgb, 59, 130, 246), 0)); }
        50% { filter: drop-shadow(0 0 8px rgba(var(--mesh-map-glow-rgb, 59, 130, 246), 0.9)); }
    }

    @keyframes mesh-map-ping {
        0% {
            transform: translate(-50%, -50%) scale(0.45);
            opacity: 0.85;
        }
        100% {
            transform: translate(-50%, -50%) scale(1.85);
            opacity: 0;
        }
    }

    @keyframes mesh-map-shake {
        0% { transform: translateX(0); }
        20% { transform: translateX(-2px); }
        40% { transform: translateX(2px); }
        60% { transform: translateX(-2px); }
        80% { transform: translateX(2px); }
        100% { transform: translateX(0); }
    }
</style>
